import React from "react";
import { FaArrowLeft, FaExternalLinkAlt } from "react-icons/fa";
import { Link, Navigate, useParams } from "react-router-dom";
import ProjectVisual from "../components/ProjectVisual";
import ProjectText from "../components/ProjectText";
import { getProjectBySlug } from "../data/projects";
import usePageMeta from "../hooks/usePageMeta";

function ProjectDetailPage() {
  const { slug } = useParams();
  const project = getProjectBySlug(slug);

  usePageMeta({
    title: project ? `${project.name} · Portfolio` : "Projet introuvable",
    description: project?.shortDescription,
  });

  if (!project) {
    return <Navigate to="/portfolio" replace />;
  }

  return (
    <div className="page-shell space-y-8 lg:space-y-10">
      <Link to="/portfolio" className="content-link inline-flex items-center">
        <FaArrowLeft className="mr-2" aria-hidden="true" />
        Retour au Portfolio
      </Link>

      <section className="hero-panel">
        <div className="grid items-center gap-8 lg:grid-cols-[1fr,1.05fr]">
          <div>
            <span className="eyebrow">{project.type}</span>
            <h1>{project.name}</h1>
            <p className="mt-5 max-w-2xl text-lg leading-8 text-muted sm:text-xl">
              <ProjectText>{project.detailDescription}</ProjectText>
            </p>
            <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="button-primary mt-7"
            >
              Voir le site
              <FaExternalLinkAlt className="ml-2" aria-hidden="true" />
              <span className="sr-only"> (nouvel onglet)</span>
            </a>
          </div>

          <div className="aspect-[16/10] overflow-hidden rounded-soft border border-line bg-white shadow-soft">
            <ProjectVisual project={project} />
          </div>
        </div>
      </section>

      <div className="grid items-start gap-6 lg:grid-cols-2">
        <section className="section-card">
          <span className="eyebrow">Le projet</span>
          <h2 className="mb-4 text-3xl">Contexte</h2>
          <div className="max-w-prose space-y-4">
            {project.context.map((paragraph) => (
              <p key={paragraph}>
                <ProjectText>{paragraph}</ProjectText>
              </p>
            ))}
          </div>
        </section>

        <section className="section-card-soft">
          <span className="eyebrow">Mon intervention</span>
          <h2 className="mb-4 text-3xl">Ce que j’ai réalisé</h2>
          <ul className="list-disc space-y-2 pl-5 marker:text-brand">
            {project.intervention.map((item) => (
              <li key={item}>
                <ProjectText>{item}</ProjectText>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="section-card">
        <span className="eyebrow">Technologies</span>
        <h2 className="mb-5 text-3xl">Stack utilisée</h2>
        <div className="flex flex-wrap gap-2">
          {project.technologies.map((technology) => (
            <span key={technology} className="skill-pill">
              {technology}
            </span>
          ))}
        </div>
      </section>

      <section className="section-card-soft">
        <span className="eyebrow">Aperçu</span>
        <h2 className="mb-5 text-3xl">Captures du projet</h2>
        {project.screenshots.length > 0 ? (
          <div className="grid items-start gap-5 md:grid-cols-2">
            {project.screenshots.map((screenshot, index) => (
              <figure
                key={screenshot.alt}
                className={`overflow-hidden rounded-soft border border-line bg-white p-2 shadow-soft sm:p-3 ${
                  screenshot.variant === "mobile"
                    ? "mx-auto w-full max-w-xs"
                    : index === 0
                      ? "md:col-span-2"
                      : ""
                }`}
              >
                <img
                  src={screenshot.src}
                  alt={screenshot.alt}
                  loading="lazy"
                  decoding="async"
                  className="h-auto w-full object-contain"
                />
              </figure>
            ))}
          </div>
        ) : (
          <div className="aspect-[16/7] min-h-48 overflow-hidden rounded-soft border border-line bg-white">
            <ProjectVisual project={project} />
          </div>
        )}
      </section>
    </div>
  );
}

export default ProjectDetailPage;
